<template>
  <div class="h-usrl">
    <div class="h-usrl__content">
      <div class="h-usrl__head">
        <div>
          <h1 class="h-usrl__title">Users</h1>
          <p class="h-usrl__sub">{{ subtitle }}</p>
        </div>
        <div class="h-usrl__actions">
          <button type="button" class="h-usrl__new" @click="openNew">
            <svg viewBox="0 0 24 24" class="h-usrl__newi"><path d="M12 5v14M5 12h14"/></svg>
            New user
          </button>
        </div>
      </div>

      <HermesUserEditorDrawer
        v-model="editorOpen"
        :api-base="apiBase"
        :user="editing"
        :meta="meta"
        @saved="onSaved"
      />

      <HermesEmptyState
        v-if="error"
        variant="error"
        title="Couldn't load users"
        :message="error"
        @retry="load"
      />
      <HermesEmptyState
        v-else-if="!loading && !rows.length"
        variant="first-run"
        title="No users yet"
        message="People who can sign in to this Hermes admin appear here."
      />
      <div v-else class="h-usrl__card">
        <HermesTabulator
          :rows="rows"
          :columns="columns"
          :loading="loading"
          storage-key="admin-users"
          :initial-sort="{ column: 'name', dir: 'asc' }"
          @row-click="openEdit"
        />
      </div>
      <p v-if="!loading && rows.length" class="h-usrl__hint">Select a row to edit the user.</p>
    </div>
  </div>
</template>

<script>
import useApi from "@apexl/icarus-core/src/services/api";

// HermesUsersList — the people who can sign in to the Hermes admin. Hermes's own
// screen: io-user 6 removed /user/list and ships nothing in its place. A
// HermesTabulator over GET /admin/users; a row opens HermesUserEditorDrawer.
//
// What each person may see and change is decided by the API. `meta` (from the
// same response) tells this screen who is looking, so it offers only what the API
// will accept: a vendor-confined admin gets no vendor picker and no platform switch.
export default {
  name: 'HermesUsersList',
  props: {
    apiBase: { type: String, default: '/api/v1' },
    componentData: { type: Object, default: () => ({}) },
  },
  data() {
    return {
      api: useApi(),
      rows: [],
      meta: { platform: false, roles: [], vendors: [], password_min_length: 12, actor_id: 0, vendor_id: null },
      loading: true,
      error: null,
      editorOpen: false,
      editing: null,
    };
  },
  computed: {
    subtitle() {
      if (this.loading) return 'Loading…';
      const n = this.rows.length;
      const inactive = this.rows.filter((r) => !r.active).length;
      const base = `${n} user${n === 1 ? '' : 's'}`;
      return inactive ? `${base} · ${inactive} inactive` : base;
    },
    columns() {
      const dash = '<span style="color:var(--h-text-tertiary)">—</span>';
      const cols = [
        {
          key: 'name', label: 'Name',
          format: (v, row) => {
            const you = row.is_self ? ' <span class="h-usrl__tag">you</span>' : '';
            return `${this.esc(v)}${you}`;
          },
        },
        { key: 'email', label: 'Email' },
        {
          key: 'role_names', label: 'Roles',
          format: (v) => (v ? this.esc(v) : dash),
        },
      ];
      if (this.meta.platform) {
        cols.push({
          key: 'vendor_name', label: 'Vendor',
          format: (v, row) => {
            const vendor = v ? this.esc(v) : dash;
            return row.is_platform_admin ? `${vendor} <span class="h-usrl__tag h-usrl__tag--gold">platform</span>` : vendor;
          },
        });
      }
      cols.push(
        {
          key: 'active', label: 'Status', width: 110, headerFilter: false,
          format: (v) => (v
            ? '<span class="h-usrl__pill h-usrl__pill--on">Active</span>'
            : '<span class="h-usrl__pill h-usrl__pill--off">Inactive</span>'),
        },
        { key: 'last_login', label: 'Last sign-in', width: 140, sorter: 'number', headerFilter: false, format: (v) => this.date(v, 'Never') },
      );
      return cols;
    },
  },
  methods: {
    async load() {
      this.loading = true;
      this.error = null;
      try {
        const data = await this.api.get(`${this.apiBase}/admin/users`);
        this.meta = { ...this.meta, ...(data.meta || {}) };
        this.rows = (data.items || []).map((u) => ({
          ...u,
          role_names: (u.roles || []).map((r) => r.name).join(', '),
        }));
      } catch (e) {
        const status = e?.response?.status;
        this.error = `Failed to load users${status ? ` (${status})` : ''}. ${e?.response?.data?.message || e?.message || ''}`.trim();
      } finally {
        this.loading = false;
      }
    },
    openNew() {
      this.editing = null;
      this.editorOpen = true;
    },
    openEdit(row) {
      this.editing = row;
      this.editorOpen = true;
    },
    onSaved() {
      this.load();
    },
    esc(v) {
      return String(v ?? '').replace(/[<>&"]/g, (ch) => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;' }[ch]));
    },
    date(v, empty = '—') {
      if (!v) return `<span style="color:var(--h-text-tertiary)">${empty}</span>`;
      const d = new Date(Number(v) * 1000);
      if (Number.isNaN(d.getTime())) return '—';
      return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    },
  },
  mounted() {
    this.load();
  },
};
</script>

<style scoped>
.h-usrl { font-family: var(--h-font-body, system-ui); color: var(--h-text-primary, #0F172A); background: var(--h-bg-content, #F8FAFC); min-height: 100vh; }
.h-usrl__content { padding: 26px 32px 64px; max-width: 1440px; width: 100%; }
.h-usrl__head { display: flex; align-items: flex-start; justify-content: space-between; gap: 24px; margin-bottom: 22px; }
.h-usrl__title { font-size: 24px; font-weight: 600; letter-spacing: -0.02em; margin: 0 0 4px; }
.h-usrl__sub { font-size: 14px; color: var(--h-text-secondary, #64748B); margin: 0; }
.h-usrl__card { background: var(--h-bg-card, #fff); border: 1px solid var(--h-border, #E2E8F0); border-radius: var(--h-radius-lg, 10px); box-shadow: var(--h-shadow-card); overflow: hidden; }
.h-usrl__hint { font-size: 12.5px; color: var(--h-text-tertiary, #94A3B8); margin: 12px 2px 0; }
.h-usrl__new { font: inherit; font-size: 13.5px; font-weight: 500; padding: 9px 15px; border-radius: var(--h-radius, 6px); border: 1px solid transparent; background: var(--h-accent, #0EA5E9); color: #fff; cursor: pointer; display: inline-flex; align-items: center; gap: 7px; }
.h-usrl__new:hover { background: var(--h-accent-hover, #0284C7); }
.h-usrl__new:focus-visible { outline: none; box-shadow: var(--h-focus-ring); }
.h-usrl__newi { width: 15px; height: 15px; stroke: currentColor; fill: none; stroke-width: 2.4; stroke-linecap: round; }
.h-usrl :deep(.tabulator-row) { cursor: pointer; }
.h-usrl :deep(.h-usrl__tag) { display: inline-block; margin-left: 6px; padding: 1px 7px; border-radius: 999px; font-size: 11px; font-weight: 600; background: var(--h-bg-subtle, #F1F5F9); color: var(--h-text-secondary, #64748B); }
.h-usrl :deep(.h-usrl__tag--gold) { background: #F5EBDA; color: #6B5324; }
.h-usrl :deep(.h-usrl__pill) { display: inline-block; padding: 2px 9px; border-radius: 999px; font-size: 12px; font-weight: 600; }
.h-usrl :deep(.h-usrl__pill--on) { background: var(--h-success-bg, #ECFDF5); color: var(--h-success-text, #047857); }
.h-usrl :deep(.h-usrl__pill--off) { background: var(--h-bg-subtle, #F1F5F9); color: var(--h-text-secondary, #64748B); }
</style>
